import { Node, mergeAttributes } from '@tiptap/core';

// Full-featured "API method" block: method, base URL, path, auth, parameters
// (path/query/header/body), request body, responses (with examples), plus
// auto-generated multi-language code samples (curl / JavaScript / Python).
// Defined here (shared) as schema + renderHTML only — SSR-safe, no React —
// so it renders identically via generateHTML on the docs site and as an atom
// preview in the editor. Attrs round-trip through a data-api-endpoint JSON attr.
// Code sample <pre><code> get Shiki-highlighted + copy buttons on the docs site.

export interface ApiParam { name: string; in: string; type: string; required: boolean; description: string }
export interface ApiResponse { status: string; description: string; example?: string }
export interface ApiEndpointAttrs {
  method: string;
  baseUrl: string;
  path: string;
  summary: string;
  description: string;
  auth: string;
  params: ApiParam[];
  requestBody: { contentType: string; example: string };
  responses: ApiResponse[];
}

const suppress = { renderHTML: () => ({}) };

const codeBlock = (lang: string, code: string) => ['pre', ['code', { class: `language-${lang}` }, code]];

/** Build curl / JS / Python samples from the endpoint definition. */
function codeSamples(a: ApiEndpointAttrs): { label: string; lang: string; code: string }[] {
  const method = String(a.method || 'GET').toUpperCase();
  const url = `${a.baseUrl || ''}${a.path || ''}`;
  const body = a.requestBody?.example?.trim();
  const ct = a.requestBody?.contentType || 'application/json';
  const auth = a.auth?.trim();

  // curl
  const curl = [`curl -X ${method} "${url}"`];
  if (auth) curl.push(`  -H "Authorization: ${auth}"`);
  if (body) { curl.push(`  -H "Content-Type: ${ct}"`); curl.push(`  -d '${body}'`); }
  const curlCode = curl.join(' \\\n');

  // JavaScript (fetch)
  const jsHeaders: string[] = [];
  if (auth) jsHeaders.push(`    "Authorization": "${auth}",`);
  if (body) jsHeaders.push(`    "Content-Type": "${ct}",`);
  const js = [`const res = await fetch("${url}", {`, `  method: "${method}",`];
  if (jsHeaders.length) js.push(`  headers: {`, ...jsHeaders, `  },`);
  if (body) js.push(`  body: JSON.stringify(${body}),`);
  js.push(`});`, `const data = await res.json();`);
  const jsCode = js.join('\n');

  // Python (requests)
  const pyHeaders: string[] = [];
  if (auth) pyHeaders.push(`"Authorization": "${auth}"`);
  if (body) pyHeaders.push(`"Content-Type": "${ct}"`);
  const py = ['import requests', ''];
  let call = `resp = requests.${method.toLowerCase()}("${url}"`;
  if (pyHeaders.length) call += `, headers={${pyHeaders.join(', ')}}`;
  if (body) call += `, json=${body}`;
  call += ')';
  py.push(call, 'print(resp.json())');
  const pyCode = py.join('\n');

  return [
    { label: 'cURL', lang: 'bash', code: curlCode },
    { label: 'JavaScript', lang: 'javascript', code: jsCode },
    { label: 'Python', lang: 'python', code: pyCode },
  ];
}

export const ApiEndpoint = Node.create({
  name: 'apiEndpoint',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      method: { default: 'GET', ...suppress },
      baseUrl: { default: '', ...suppress },
      path: { default: '', ...suppress },
      summary: { default: '', ...suppress },
      description: { default: '', ...suppress },
      auth: { default: '', ...suppress },
      params: { default: [] as ApiParam[], ...suppress },
      requestBody: { default: { contentType: 'application/json', example: '' }, ...suppress },
      responses: { default: [] as ApiResponse[], ...suppress },
    };
  },

  parseHTML() {
    return [{
      tag: 'div[data-api-endpoint]',
      getAttrs: (el: any) => {
        try { return JSON.parse(el.getAttribute('data-api-endpoint') || '{}'); } catch { return {}; }
      },
    }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const a = node.attrs as ApiEndpointAttrs;
    const method = String(a.method || 'GET').toUpperCase();
    const children: any[] = [
      ['div', { class: 'api-head' },
        ['span', { class: `api-method m-${method.toLowerCase()}` }, method],
        ['code', { class: 'api-path' }, `${a.baseUrl || ''}${a.path || ''}`],
      ],
    ];
    if (a.summary) children.push(['div', { class: 'api-summary' }, a.summary]);
    if (a.description) children.push(['p', { class: 'api-desc' }, a.description]);
    if (a.auth) children.push(['div', { class: 'api-auth' }, ['span', { class: 'api-auth-label' }, 'Auth'], ['code', {}, a.auth]]);

    const params = Array.isArray(a.params) ? a.params : [];
    if (params.length) {
      children.push(['div', { class: 'api-label' }, 'Parameters']);
      children.push(['table', { class: 'api-table' },
        ['thead', ['tr', ['th', 'Name'], ['th', 'In'], ['th', 'Type'], ['th', 'Required'], ['th', 'Description']]],
        ['tbody', ...params.map((p) => ['tr',
          ['td', ['code', {}, p.name || '']], ['td', p.in || ''], ['td', p.type || ''],
          ['td', p.required ? 'Yes' : 'No'], ['td', p.description || ''],
        ])],
      ]);
    }

    const rb = a.requestBody;
    if (rb?.example?.trim()) {
      children.push(['div', { class: 'api-label' }, `Request body · ${rb.contentType || 'application/json'}`]);
      children.push(codeBlock('json', rb.example.trim()));
    }

    const responses = Array.isArray(a.responses) ? a.responses : [];
    if (responses.length) {
      children.push(['div', { class: 'api-label' }, 'Responses']);
      children.push(['table', { class: 'api-table' },
        ['thead', ['tr', ['th', 'Status'], ['th', 'Description']]],
        ['tbody', ...responses.map((r) => ['tr', ['td', ['code', {}, String(r.status || '')]], ['td', r.description || '']])],
      ]);
      for (const r of responses.filter((x) => x.example?.trim())) {
        children.push(['div', { class: 'api-label' }, `Example response · ${r.status}`]);
        children.push(codeBlock('json', r.example!.trim()));
      }
    }

    // Auto-generated code samples.
    children.push(['div', { class: 'api-label' }, 'Code samples']);
    children.push(['div', { class: 'api-samples' },
      ...codeSamples(a).map((s) => ['div', { class: 'api-sample' },
        ['div', { class: 'api-sample-lang' }, s.label],
        codeBlock(s.lang, s.code),
      ]),
    ]);

    return ['div', mergeAttributes(HTMLAttributes, { 'data-api-endpoint': JSON.stringify(node.attrs), class: 'api-endpoint' }), ...children];
  },
});
